import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Activity, AlertTriangle, Building2, Clock, CheckCircle2, PauseCircle, Landmark, XCircle, ShieldCheck } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { DataTable, type Column } from '@/components/system/DataTable';
import {
  useIFAdminFinanciamentos,
  useIFHealthCheck,
  useIFContratosDisponiveis,
  useIFCriarFinanciamento,
  useIFAlterarStatusFinanciamento,
  useIFInstituicoes,
  useIFCriarInstituicao,
  useIFConfirmarVinculo,
  useIFSuspenderVinculo,
  useIFEncerrarVinculo,
  type IFFinanciamento,
  type IFInstituicao,
} from '../hooks/useIFAdminContratos';
import { useIFAlertasCount } from '../hooks/useIFAlertas';
import { format } from 'date-fns';

const statusBadge = (status: string) => {
  const map: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    ativo: { variant: 'default', label: 'Ativo' },
    provisorio: { variant: 'secondary', label: 'Provisório' },
    pendente: { variant: 'outline', label: 'Pendente' },
    suspenso: { variant: 'secondary', label: 'Suspenso' },
    encerrado: { variant: 'outline', label: 'Encerrado' },
  };
  const m = map[status] || { variant: 'outline' as const, label: status };
  return <Badge variant={m.variant} className="text-xs">{m.label}</Badge>;
};

const tipoVinculoBadge = (tipo: string) => {
  if (tipo === 'financiamento') return <Badge variant="default" className="text-xs">Financiamento</Badge>;
  return <Badge variant="outline" className="text-xs">Monitoramento</Badge>;
};

const instColumns: Column<IFInstituicao>[] = [
  { key: 'nome', label: 'Nome', render: r => <span className="font-medium">{r.nome}</span> },
  { key: 'cnpj', label: 'CNPJ', render: r => <span className="text-sm text-muted-foreground">{r.cnpj || '—'}</span> },
  { key: 'contato', label: 'Contato', render: r => <span className="text-sm">{r.contato_nome || '—'}</span> },
  { key: 'contratos', label: 'Contratos', align: 'center', render: r => r.total_contratos ?? 0 },
  { key: 'status', label: 'Status', align: 'center', render: r => (
    <Badge variant={r.ativo ? 'default' : 'secondary'} className="text-xs">{r.ativo ? 'Ativo' : 'Inativo'}</Badge>
  )},
];

const InstituicoesTab: React.FC<{ instituicoes: IFInstituicao[] }> = ({ instituicoes }) => {
  const criarInst = useIFCriarInstituicao();
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ nome: '', cnpj: '', contato_nome: '', contato_email: '' });

  const handleCreate = async () => {
    if (!form.nome) { toast.error('Nome é obrigatório.'); return; }
    try {
      await criarInst.mutateAsync(form);
      toast.success('Instituição criada.');
      setShowDialog(false);
      setForm({ nome: '', cnpj: '', contato_nome: '', contato_email: '' });
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowDialog(true)} className="gap-2" size="sm">
          <Plus className="h-4 w-4" /> Nova Instituição
        </Button>
      </div>
      <DataTable
        columns={instColumns}
        data={instituicoes}
        keyExtractor={r => r.id}
        emptyText="Nenhuma instituição financeira cadastrada."
      />
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova Instituição Financeira</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome</Label><Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Banco do Brasil" /></div>
            <div><Label>CNPJ</Label><Input value={form.cnpj} onChange={e => setForm(f => ({ ...f, cnpj: e.target.value }))} placeholder="00.000.000/0001-00" /></div>
            <div><Label>Contato</Label><Input value={form.contato_nome} onChange={e => setForm(f => ({ ...f, contato_nome: e.target.value }))} /></div>
            <div><Label>Email</Label><Input value={form.contato_email} onChange={e => setForm(f => ({ ...f, contato_email: e.target.value }))} type="email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={criarInst.isPending}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const IFAdminPage: React.FC = () => {
  const { data: financiamentos = [], isLoading: loadingFin } = useIFAdminFinanciamentos();
  const { data: health } = useIFHealthCheck();
  const { data: alertasCount = 0 } = useIFAlertasCount();
  const { data: instituicoes = [], isLoading: loadingInst } = useIFInstituicoes();
  const { data: contratosDisponiveis = [] } = useIFContratosDisponiveis();
  const criarFinanciamento = useIFCriarFinanciamento();
  const confirmarVinculo = useIFConfirmarVinculo();
  const suspenderVinculo = useIFSuspenderVinculo();
  const encerrarVinculo = useIFEncerrarVinculo();
  const [showDialog, setShowDialog] = useState(false);
  const [showEncerrarDialog, setShowEncerrarDialog] = useState<string | null>(null);
  const [motivoEncerramento, setMotivoEncerramento] = useState('');
  const [form, setForm] = useState({ contrato_id: '', instituicao_id: '', tipo_vinculo: 'monitoramento' as 'monitoramento' | 'financiamento', data_inicio: '' });

  const isLoading = loadingFin || loadingInst;
  const ativos = financiamentos.filter(f => f.status === 'ativo').length;

  const handleCreate = async () => {
    if (!form.contrato_id || !form.instituicao_id) { toast.error('Selecione instituição e contrato.'); return; }
    try {
      await criarFinanciamento.mutateAsync({
        contrato_id: form.contrato_id,
        instituicao_id: form.instituicao_id,
        tipo_vinculo: form.tipo_vinculo,
        data_inicio_financiamento: form.data_inicio || null,
      });
      toast.success('Vínculo criado.');
      setShowDialog(false);
      setForm({ contrato_id: '', instituicao_id: '', tipo_vinculo: 'monitoramento', data_inicio: '' });
    } catch (err: any) { toast.error(err.message); }
  };

  const handleConfirmar = async (id: string) => {
    try {
      await confirmarVinculo.mutateAsync(id);
      toast.success('Vínculo confirmado — acesso permanente concedido.');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleSuspender = async (id: string) => {
    try {
      await suspenderVinculo.mutateAsync(id);
      toast.success('Vínculo suspenso.');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleEncerrar = async () => {
    if (!showEncerrarDialog || !motivoEncerramento.trim()) { toast.error('Informe o motivo do encerramento.'); return; }
    try {
      await encerrarVinculo.mutateAsync({ id: showEncerrarDialog, motivo: motivoEncerramento.trim() });
      toast.success('Vínculo encerrado.');
      setShowEncerrarDialog(null);
      setMotivoEncerramento('');
    } catch (err: any) { toast.error(err.message); }
  };

  const renderActions = (r: IFFinanciamento) => {
    const actions: React.ReactNode[] = [];
    if (r.status === 'provisorio') {
      actions.push(
        <Button key="confirm" variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleConfirmar(r.id); }} className="gap-1 text-xs" style={{ color: 'hsl(var(--success))' }}>
          <ShieldCheck className="h-3.5 w-3.5" /> Confirmar
        </Button>
      );
    }
    if (r.status === 'ativo' || r.status === 'provisorio') {
      actions.push(
        <Button key="suspend" variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleSuspender(r.id); }} className="gap-1 text-xs">
          <PauseCircle className="h-3.5 w-3.5" /> Suspender
        </Button>
      );
    }
    if (r.status === 'suspenso') {
      actions.push(
        <Button key="reactivate" variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleConfirmar(r.id); }} className="gap-1 text-xs" style={{ color: 'hsl(var(--success))' }}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Reativar
        </Button>
      );
    }
    if (r.status !== 'encerrado') {
      actions.push(
        <Button key="end" variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setShowEncerrarDialog(r.id); setMotivoEncerramento(''); }} className="gap-1 text-xs text-destructive">
          <XCircle className="h-3.5 w-3.5" /> Encerrar
        </Button>
      );
    }
    return <div className="flex gap-1">{actions}</div>;
  };

  const vinculoColumns: Column<IFFinanciamento>[] = [
    { key: 'inst', label: 'Instituição', render: r => <span className="font-medium">{r.instituicao_nome}</span> },
    { key: 'contrato', label: 'Contrato', render: r => <span className="max-w-[200px] truncate block">{r.contrato_nome}</span> },
    { key: 'tipo', label: 'Tipo', align: 'center', render: r => tipoVinculoBadge(r.tipo_vinculo) },
    { key: 'status', label: 'Status', align: 'center', render: r => statusBadge(r.status) },
    { key: 'expira', label: 'Expira', render: r => <span className="text-sm text-muted-foreground">{r.expires_at ? format(new Date(r.expires_at), 'dd/MM/yyyy') : '—'}</span> },
    { key: 'criado', label: 'Criado', render: r => <span className="text-sm text-muted-foreground">{format(new Date(r.created_at), 'dd/MM/yyyy')}</span> },
    { key: 'acoes', label: 'Ações', align: 'center', render: renderActions },
  ];

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
          title="Administração de Vínculos"
          subtitle="Gerenciar instituições financeiras e vínculos de monitoramento"
          actions={<Button onClick={() => setShowDialog(true)} className="gap-2"><Plus className="h-4 w-4" /> Novo Vínculo</Button>}
        />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Activity className="h-4 w-4" />Snapshot</div>
            <div className="flex items-center gap-2 mt-1">
              {health?.status === 'healthy' ? <CheckCircle2 className="h-5 w-5" style={{ color: 'hsl(var(--success))' }} /> : <Clock className="h-5 w-5 text-muted-foreground" />}
              <span className="text-lg font-bold capitalize">{health?.status || '—'}</span>
            </div>
            {health?.hours_since_last != null && <p className="text-xs text-muted-foreground mt-1">Há {health.hours_since_last}h</p>}
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Landmark className="h-4 w-4" />Instituições</div>
            <p className="text-2xl font-bold mt-1">{instituicoes.filter(i => i.ativo).length}</p>
            <p className="text-xs text-muted-foreground">ativas</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Building2 className="h-4 w-4" />Contratos</div>
            <p className="text-2xl font-bold mt-1">{ativos}</p>
            <p className="text-xs text-muted-foreground">monitorados ativos</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><AlertTriangle className="h-4 w-4" />Alertas</div>
            <p className="text-2xl font-bold mt-1 text-destructive">{alertasCount}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4" />Com Histórico</div>
            <p className="text-2xl font-bold mt-1">{health?.contratos_com_historico ?? 0}</p>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="vinculos">
          <TabsList>
            <TabsTrigger value="vinculos">Vínculos IF ↔ Contratos</TabsTrigger>
            <TabsTrigger value="instituicoes">Instituições Financeiras</TabsTrigger>
          </TabsList>

          <TabsContent value="vinculos">
            <SectionCard title="Vínculos de Monitoramento" icon={Building2}>
              <DataTable
                columns={vinculoColumns}
                data={financiamentos}
                keyExtractor={r => r.id}
                emptyText="Nenhum vínculo registrado."
              />
            </SectionCard>
          </TabsContent>

          <TabsContent value="instituicoes">
            <SectionCard title="Instituições Financeiras Cadastradas" icon={Landmark}>
              <InstituicoesTab instituicoes={instituicoes} />
            </SectionCard>
          </TabsContent>
        </Tabs>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo Vínculo de Monitoramento</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Instituição Financeira</Label>
                <Select value={form.instituicao_id} onValueChange={v => setForm(f => ({ ...f, instituicao_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione a instituição" /></SelectTrigger>
                  <SelectContent>
                    {instituicoes.filter(i => i.ativo).map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contrato</Label>
                <Select value={form.contrato_id} onValueChange={v => setForm(f => ({ ...f, contrato_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione o contrato" /></SelectTrigger>
                  <SelectContent>
                    {contratosDisponiveis.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Vínculo</Label>
                <Select value={form.tipo_vinculo} onValueChange={v => setForm(f => ({ ...f, tipo_vinculo: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monitoramento">Monitoramento</SelectItem>
                    <SelectItem value="financiamento">Financiamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data de Início</Label>
                <Input type="date" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={criarFinanciamento.isPending}>
                {criarFinanciamento.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Vincular
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!showEncerrarDialog} onOpenChange={v => { if (!v) { setShowEncerrarDialog(null); setMotivoEncerramento(''); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Encerrar Vínculo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Informe o motivo do encerramento. Esta ação é irreversível.</p>
              <Textarea value={motivoEncerramento} onChange={e => setMotivoEncerramento(e.target.value)} placeholder="Motivo do encerramento..." rows={3} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEncerrarDialog(null); setMotivoEncerramento(''); }}>Cancelar</Button>
              <Button variant="destructive" onClick={handleEncerrar} disabled={encerrarVinculo.isPending || !motivoEncerramento.trim()}>
                {encerrarVinculo.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Encerrar Vínculo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default IFAdminPage;
